// SPDX-License-Identifier: Apache-2.0
// File: src/ui/components/PrecisionHorizontalSlider.tsx
import React, { useRef, useState } from 'react';
import { PocketGridRetroPalette } from '../theme/PocketGridRetroTheme';

interface PrecisionHorizontalSliderProps {
  label: string;
  formattedValue: string;
  value: number; // Normalized [0..1]
  defaultValue?: number;
  accentColor?: string;
  midiCcNumber?: number;
  isMidiLearning?: boolean;
  bipolar?: boolean;
  springBack?: boolean;
  springBackTarget?: number;
  holdActive?: boolean;
  compact?: boolean;
  onToggleMidiLearn?: () => void;
  onChange: (nextValue: number) => void;
  onLabelClick?: () => void;
}

export const PrecisionHorizontalSlider: React.FC<PrecisionHorizontalSliderProps> = ({
  label,
  formattedValue,
  value,
  defaultValue = 0.5,
  accentColor = PocketGridRetroPalette.tungstenAmber,
  midiCcNumber,
  isMidiLearning = false,
  bipolar = false,
  springBack = false,
  springBackTarget,
  holdActive = false,
  compact = false,
  onToggleMidiLearn,
  onChange,
  onLabelClick,
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFineTune, setIsFineTune] = useState(false);

  const dragStateRef = useRef<{
    active: boolean;
    pointerId: number;
    startX: number;
    startY: number;
    prevX: number;
    currentVal: number;
    lastTapMs: number;
  }>({
    active: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    prevX: 0,
    currentVal: value,
    lastTapMs: 0,
  });

  dragStateRef.current.currentVal = value;

  const computeDirectPositionValue = (clientX: number): number => {
    const trackEl = trackRef.current;
    if (!trackEl) return value;
    const rect = trackEl.getBoundingClientRect();
    const pad = compact ? 5 : 8;
    const usableWidth = Math.max(24, rect.width - pad * 2);
    const relX = clientX - (rect.left + pad);
    return Math.max(0.0, Math.min(1.0, relX / usableWidth));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const now = performance.now();
    if (now - dragStateRef.current.lastTapMs < 260) {
      // Double-tap resets to studio default
      dragStateRef.current.currentVal = defaultValue;
      onChange(defaultValue);
      dragStateRef.current.lastTapMs = 0;
      return;
    }
    dragStateRef.current.lastTapMs = now;

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Ignore pointer capture errors on synthetic events
    }

    const clickedVal = computeDirectPositionValue(e.clientX);
    const clickedNearCap = Math.abs(clickedVal - value) < 0.06;
    const initialVal = clickedNearCap ? value : clickedVal;

    dragStateRef.current.active = true;
    dragStateRef.current.pointerId = e.pointerId;
    dragStateRef.current.startX = e.clientX;
    dragStateRef.current.startY = e.clientY;
    dragStateRef.current.prevX = e.clientX;
    dragStateRef.current.currentVal = initialVal;

    setIsDragging(true);
    setIsFineTune(false);

    if (!clickedNearCap) {
      onChange(initialVal);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.active || e.pointerId !== dragStateRef.current.pointerId) return;
    e.stopPropagation();

    const trackEl = trackRef.current;
    if (!trackEl) return;

    const vertDist = Math.max(0, Math.abs(e.clientY - dragStateRef.current.startY) - 28);
    const fineMode = vertDist > 4;
    if (fineMode !== isFineTune) {
      setIsFineTune(fineMode);
    }

    if (!fineMode) {
      const nextVal = computeDirectPositionValue(e.clientX);
      dragStateRef.current.prevX = e.clientX;
      dragStateRef.current.currentVal = nextVal;
      onChange(nextVal);
    } else {
      const width = Math.max(40, trackEl.getBoundingClientRect().width - 16);
      const dx = e.clientX - dragStateRef.current.prevX;
      dragStateRef.current.prevX = e.clientX;
      const gearReduction = 1.0 / (1.0 + 0.05 * vertDist);
      const delta = (dx / width) * gearReduction;
      const nextVal = Math.max(0.0, Math.min(1.0, dragStateRef.current.currentVal + delta));
      dragStateRef.current.currentVal = nextVal;
      onChange(nextVal);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId === dragStateRef.current.pointerId) {
      dragStateRef.current.active = false;
      dragStateRef.current.pointerId = -1;
      setIsDragging(false);
      setIsFineTune(false);

      if (springBack && !holdActive) {
        const target = springBackTarget !== undefined ? springBackTarget : defaultValue;
        dragStateRef.current.currentVal = target;
        onChange(target);
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const step = e.shiftKey ? 0.005 : 0.025;
    const dir = e.deltaY < 0 || e.deltaX > 0 ? 1 : -1;
    const nextVal = Math.max(0.0, Math.min(1.0, value + dir * step));
    onChange(nextVal);
  };

  const clampedPct = Math.max(0, Math.min(100, value * 100));

  return (
    <div
      onWheel={handleWheel}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        padding: compact ? '1px 0' : '4px 0',
        userSelect: 'none',
        touchAction: 'none',
        borderRadius: '4px',
        background: isMidiLearning ? 'rgba(217, 148, 59, 0.14)' : 'transparent',
        outline: isMidiLearning ? `1.5px dashed ${PocketGridRetroPalette.tungstenAmber}` : 'none',
      }}
    >
      {/* Label, Formatted Readout & MIDI CC Learn Badge Row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: compact ? '8px' : '11.5px',
          fontWeight: 800,
          letterSpacing: '0.02em',
          color: isDragging
            ? PocketGridRetroPalette.incandescentCream
            : PocketGridRetroPalette.warmIvoryText,
          marginBottom: compact ? '0px' : '4px',
          lineHeight: compact ? 1.0 : 1.25,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span
          onClick={onLabelClick}
          title={onLabelClick ? 'Click to reassign slider parameter' : undefined}
          style={{
            cursor: onLabelClick ? 'pointer' : 'default',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}{' '}
          <span
            style={{
              color: isDragging ? accentColor : PocketGridRetroPalette.mutedPewterText,
              fontSize: compact ? '7.5px' : '11px',
              fontWeight: 700,
            }}
          >
            ({formattedValue})
          </span>
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '2px' : '4px', flexShrink: 0 }}>
          {isFineTune && (
            <span
              style={{
                fontSize: compact ? '6.5px' : '9px',
                padding: compact ? '0 2px' : '1px 4px',
                borderRadius: '3px',
                background: '#2C2318',
                color: PocketGridRetroPalette.tungstenAmber,
                border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
              }}
            >
              FINE
            </span>
          )}

          {onToggleMidiLearn && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleMidiLearn();
              }}
              title="Click, then move any knob or slider on your MIDI controller to map it!"
              style={{
                fontSize: compact ? '7px' : '9.5px',
                fontWeight: 700,
                padding: compact ? '0 3px' : '0 6px',
                height: compact ? '12px' : '18px',
                lineHeight: compact ? '12px' : '18px',
                borderRadius: '3px',
                cursor: 'pointer',
                background: isMidiLearning ? PocketGridRetroPalette.tungstenAmber : '#1B1C20',
                color: isMidiLearning ? '#141517' : PocketGridRetroPalette.mutedPewterText,
                border: `1px solid ${
                  isMidiLearning
                    ? PocketGridRetroPalette.incandescentCream
                    : PocketGridRetroPalette.panelBorder
                }`,
              }}
            >
              {isMidiLearning
                ? '●'
                : midiCcNumber !== undefined
                ? `CC ${midiCcNumber}`
                : 'CC'}
            </button>
          )}
        </div>
      </div>

      {/* Interactive Horizontal Track + Brushed Aluminum Cap + Engraved Tick Marks */}
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: 'relative',
          height: compact ? '18px' : '28px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'ew-resize',
          padding: compact ? '0 5px' : '0 8px',
          boxSizing: 'border-box',
        }}
      >
        {/* Engraved Scale Graduations Underneath Track */}
        <div
          style={{
            position: 'absolute',
            bottom: '0px',
            left: compact ? '5px' : '8px',
            right: compact ? '5px' : '8px',
            height: compact ? '2.5px' : '5px',
            display: 'flex',
            justifyContent: 'space-between',
            pointerEvents: 'none',
          }}
        >
          {Array.from({ length: 17 }).map((_, idx) => (
            <div
              key={idx}
              style={{
                width: idx % 4 === 0 && !compact ? '1.5px' : '1px',
                height: idx % 4 === 0 ? (compact ? '2.5px' : '5px') : (compact ? '1px' : '2.5px'),
                background: idx % 4 === 0 ? '#78736A' : '#43403C',
              }}
            />
          ))}
        </div>

        {/* Recessed Oxide Track Groove */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: compact ? '5px' : '8px',
            background: PocketGridRetroPalette.recessedTrack,
            borderRadius: compact ? '2.5px' : '4px',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.9)',
            border: `1px solid ${isDragging || isMidiLearning ? accentColor : '#2D2F34'}`,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {/* Center Zero Notch for Bipolar Sliders */}
          {bipolar && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: compact ? '1.5px' : '2px',
                transform: 'translateX(-50%)',
                background: '#686B74',
                zIndex: 2,
              }}
            />
          )}

          {/* Illuminated Retro LED Bar Fill (supports Bipolar Outward Fill from Center) */}
          <div
            style={{
              position: 'absolute',
              left: bipolar ? `${Math.min(50, clampedPct)}%` : '0%',
              width: bipolar ? `${Math.abs(clampedPct - 50)}%` : `${clampedPct}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${accentColor}99 0%, ${accentColor} 100%)`,
              boxShadow: `0 0 ${compact ? '4px' : '8px'} ${accentColor}AA`,
              borderRadius: compact ? '2.5px' : '4px',
            }}
          />
        </div>

        {/* Machined Brushed-Aluminum Fader Cap (Enlarged on mobile for comfortable finger grabbing) */}
        <div
          style={{
            position: 'absolute',
            left: compact
              ? `calc(5px + (100% - 10px) * ${clampedPct / 100} - 11px)`
              : `calc(8px + (100% - 16px) * ${clampedPct / 100} - 13px)`,
            width: compact ? '22px' : '26px',
            height: compact ? '15px' : '18px',
            borderRadius: compact ? '3px' : '4px',
            background: isDragging
              ? 'linear-gradient(180deg, #FFFDF7 0%, #D8D2C5 50%, #A69F91 100%)'
              : 'linear-gradient(180deg, #ECE8DF 0%, #B8B2A6 50%, #8E887C 100%)',
            border: `1px solid ${isDragging ? accentColor : '#4A4740'}`,
            boxShadow: isDragging
              ? `0 0 8px ${accentColor}88, 0 2px 5px rgba(0,0,0,0.85)`
              : '0 2px 4px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: compact ? '2px' : '2.5px',
            pointerEvents: 'none',
          }}
        >
          {/* Tactile Grip Grooves */}
          <div style={{ width: '1px', height: compact ? '9px' : '12px', background: '#4A4740' }} />
          <div
            style={{
              width: compact ? '1.5px' : '2px',
              height: compact ? '11px' : '14px',
              background: isDragging ? accentColor : '#2C2A26',
              borderRadius: '1px',
            }}
          />
          <div style={{ width: '1px', height: compact ? '9px' : '12px', background: '#4A4740' }} />
        </div>
      </div>
    </div>
  );
};
